import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { api, ApiError } from '../api/client.js'
import { useAuth } from '../auth/AuthContext.js'
import { useNav } from '../nav/NavContext.js'
import { MasterScreen, type MasterScreenConfig } from '../masters/MasterScreen.js'
import { accountingHeadsConfig, branchesConfig, chargeTypesConfig, expenseHeadsConfig, vehicleTypesConfig } from '../masters/configs.js'

const errMsg = (e: unknown, fallback: string): string => (e instanceof ApiError ? e.message : fallback)

/** Stage 12 §3.2 grouping. The whole area is Owner-only (tenant_settings.update). */
const TABS = [
  { key: 'organization', label: 'Organization' },
  { key: 'users', label: 'Users & Access' },
  { key: 'language', label: 'Language' },
  { key: 'masters', label: 'Operational Masters' },
  { key: 'integrations', label: 'Integrations' },
  { key: 'audit', label: 'Audit Trail' },
] as const
type TabKey = (typeof TABS)[number]['key']

export function SettingsPage() {
  const { params } = useNav()
  const initial = TABS.find((t) => t.key === params.tab)?.key ?? 'organization'
  const [tab, setTab] = useState<TabKey>(initial)
  return (
    <div>
      <h1 className="tf-login-title">Settings</h1>
      <nav className="tf-tabs">
        {TABS.map((t) => (
          <button key={t.key} type="button" className={tab === t.key ? 'tf-tab-active' : ''} onClick={() => setTab(t.key)}>{t.label}</button>
        ))}
      </nav>
      {tab === 'organization' && <OrganizationTab />}
      {tab === 'users' && <UsersTab />}
      {tab === 'language' && <LanguageTab />}
      {tab === 'masters' && <MastersTab />}
      {tab === 'integrations' && <IntegrationsTab />}
      {tab === 'audit' && <AuditLink />}
    </div>
  )
}

// --- Organization -----------------------------------------------------------

interface Org {
  company_name: string
  gstin: string | null
  pan_no: string | null
  contact_email: string | null
  website: string | null
  letterhead_phones: string[]
  bank_details: { account_name?: string; account_no?: string; ifsc?: string; bank?: string; branch?: string } | null
}

function OrganizationTab() {
  const [org, setOrg] = useState<Org | null>(null)
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null)
  useEffect(() => {
    api.get<Org>('/settings/organization').then(setOrg).catch((e) => setMsg({ ok: false, text: errMsg(e, 'Could not load') }))
  }, [])

  const save = async (e: FormEvent) => {
    e.preventDefault()
    if (!org) return
    setMsg(null)
    const bank = org.bank_details ?? {}
    const hasBank = Object.values(bank).some((v) => v)
    try {
      const saved = await api.put<Org>('/settings/organization', {
        company_name: org.company_name,
        gstin: org.gstin?.trim() ? org.gstin.trim().toUpperCase() : null,
        pan_no: org.pan_no?.trim() ? org.pan_no.trim() : null,
        contact_email: org.contact_email?.trim() ? org.contact_email.trim() : null,
        website: org.website?.trim() ? org.website.trim() : null,
        letterhead_phones: org.letterhead_phones.map((p) => p.trim()).filter(Boolean),
        bank_details: hasBank ? bank : null,
      })
      setOrg(saved)
      setMsg({ ok: true, text: 'Saved. New LRs and invoices will print these details.' })
    } catch (err) {
      setMsg({ ok: false, text: errMsg(err, 'Save failed') })
    }
  }

  if (!org) return <p className="tf-note">{msg?.text ?? 'Loading…'}</p>
  const bank = org.bank_details ?? {}
  const setBank = (k: string, v: string) => setOrg({ ...org, bank_details: { ...bank, [k]: v } })
  const p0 = org.letterhead_phones[0] ?? ''
  const p1 = org.letterhead_phones[1] ?? ''

  return (
    <>
      <form className="tf-card tf-form" onSubmit={save}>
        <h2>Company profile, letterhead &amp; bank details</h2>
        <div className="tf-grid-2">
          <label className="tf-field">Company name<input required value={org.company_name} onChange={(e) => setOrg({ ...org, company_name: e.target.value })} /></label>
          <label className="tf-field">GSTIN<input value={org.gstin ?? ''} onChange={(e) => setOrg({ ...org, gstin: e.target.value })} placeholder="15-character GSTIN" /></label>
          <label className="tf-field">PAN<input value={org.pan_no ?? ''} onChange={(e) => setOrg({ ...org, pan_no: e.target.value })} /></label>
          <label className="tf-field">Contact email<input type="email" value={org.contact_email ?? ''} onChange={(e) => setOrg({ ...org, contact_email: e.target.value })} /></label>
          <label className="tf-field">Website<input value={org.website ?? ''} onChange={(e) => setOrg({ ...org, website: e.target.value })} /></label>
          <label className="tf-field">Letterhead phone 1<input value={p0} onChange={(e) => setOrg({ ...org, letterhead_phones: [e.target.value, p1] })} /></label>
          <label className="tf-field">Letterhead phone 2<input value={p1} onChange={(e) => setOrg({ ...org, letterhead_phones: [p0, e.target.value] })} /></label>
        </div>
        <h2 style={{ marginTop: '1rem' }}>Bank details (printed on invoices)</h2>
        <div className="tf-grid-2">
          <label className="tf-field">Account name<input value={bank.account_name ?? ''} onChange={(e) => setBank('account_name', e.target.value)} /></label>
          <label className="tf-field">Account number<input value={bank.account_no ?? ''} onChange={(e) => setBank('account_no', e.target.value)} /></label>
          <label className="tf-field">IFSC<input value={bank.ifsc ?? ''} onChange={(e) => setBank('ifsc', e.target.value)} /></label>
          <label className="tf-field">Bank<input value={bank.bank ?? ''} onChange={(e) => setBank('bank', e.target.value)} /></label>
          <label className="tf-field">Branch<input value={bank.branch ?? ''} onChange={(e) => setBank('branch', e.target.value)} /></label>
        </div>
        {msg && <p className={msg.ok ? 'tf-note' : 'tf-error'}>{msg.text}</p>}
        <div className="tf-form-actions"><button className="tf-button-primary" type="submit">Save</button></div>
      </form>
      <h2 style={{ marginTop: '1.5rem' }}>Branches</h2>
      <MasterScreen config={branchesConfig} />
    </>
  )
}

// --- Users & Access ---------------------------------------------------------

interface UserRow { id: string; name: string; phone: string; is_active: boolean; last_login_at: string | null }
interface RoleRow { id: string; name: string; description: string | null; is_system: boolean; permissions: string[] }
interface BranchRow { id: string; name: string }

function UsersTab() {
  const [users, setUsers] = useState<UserRow[]>([])
  const [roles, setRoles] = useState<RoleRow[]>([])
  const [branches, setBranches] = useState<BranchRow[]>([])
  const [form, setForm] = useState({ name: '', phone: '', role_id: '', branch_id: '' })
  const [created, setCreated] = useState<{ name: string; password: string } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [openRole, setOpenRole] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const [u, r, b] = await Promise.all([
        api.get<{ rows: UserRow[] }>('/users?pageSize=100&status=all'),
        api.get<RoleRow[]>('/roles'),
        api.get<{ rows: BranchRow[] }>('/branches?pageSize=100'),
      ])
      setUsers(u.rows); setRoles(r); setBranches(b.rows)
    } catch (e) {
      setError(errMsg(e, 'Could not load users'))
    }
  }, [])
  useEffect(() => { void load() }, [load])

  const create = async (e: FormEvent) => {
    e.preventDefault()
    setError(null); setCreated(null)
    try {
      const r = await api.post<{ temporary_password: string }>('/users', {
        name: form.name, phone: form.phone, role_id: form.role_id, branch_id: form.branch_id || null,
      })
      setCreated({ name: form.name, password: r.temporary_password })
      setForm({ name: '', phone: '', role_id: '', branch_id: '' })
      await load()
    } catch (err) {
      setError(errMsg(err, 'Could not create the user'))
    }
  }

  const deactivate = async (u: UserRow) => {
    if (!window.confirm(`Deactivate ${u.name}? They will no longer be able to sign in.`)) return
    try { await api.patch(`/users/${u.id}/deactivate`); await load() } catch (err) { setError(errMsg(err, 'Could not deactivate')) }
  }

  return (
    <>
      <form className="tf-card tf-form" onSubmit={create}>
        <h2>Add a user</h2>
        <div className="tf-grid-2">
          <label className="tf-field">Name<input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
          <label className="tf-field">Mobile (login)<input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="10-digit mobile" /></label>
          <label className="tf-field">Role
            <select required value={form.role_id} onChange={(e) => setForm({ ...form, role_id: e.target.value })}>
              <option value="">Select…</option>
              {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </label>
          <label className="tf-field">Branch
            <select value={form.branch_id} onChange={(e) => setForm({ ...form, branch_id: e.target.value })}>
              <option value="">All branches</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </label>
        </div>
        {error && <p className="tf-error">{error}</p>}
        {created && (
          <p className="tf-override-banner">
            {created.name} can sign in with the temporary password <strong>{created.password}</strong> and will be asked to change it. It is shown only once — note it down now.
          </p>
        )}
        <div className="tf-form-actions"><button className="tf-button-primary" type="submit">Create user</button></div>
      </form>

      <table className="tf-table" style={{ marginTop: '1.25rem' }}>
        <thead><tr><th>Name</th><th>Mobile</th><th>Last sign-in</th><th>Status</th><th /></tr></thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id}>
              <td>{u.name}</td><td>{u.phone}</td>
              <td>{u.last_login_at ? new Date(u.last_login_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : 'Never'}</td>
              <td><span className={`tf-pill ${u.is_active ? 'tf-pill-ok' : 'tf-pill-bad'}`}>{u.is_active ? 'Active' : 'Inactive'}</span></td>
              <td className="tf-row-actions">{u.is_active && <button type="button" onClick={() => void deactivate(u)}>Deactivate</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2 style={{ marginTop: '1.5rem' }}>Roles &amp; permissions</h2>
      <p className="tf-note">Roles are fixed templates for now; this list shows what each role can do.</p>
      <table className="tf-table">
        <thead><tr><th>Role</th><th>Description</th><th>Permissions</th></tr></thead>
        <tbody>
          {roles.map((r) => (
            <tr key={r.id}>
              <td>{r.name}</td>
              <td>{r.description}</td>
              <td>
                <button type="button" className="tf-button-link" onClick={() => setOpenRole(openRole === r.id ? null : r.id)}>{r.permissions.length} permissions</button>
                {openRole === r.id && <div className="tf-note" style={{ marginTop: '0.35rem' }}>{[...r.permissions].sort().join(', ')}</div>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  )
}

// --- Language ---------------------------------------------------------------

interface Lang { code: string; label: string; enabled: boolean; is_default: boolean }

function LanguageTab() {
  const [langs, setLangs] = useState<Lang[]>([])
  const [error, setError] = useState<string | null>(null)
  const load = useCallback(() => { api.get<Lang[]>('/languages').then(setLangs).catch((e) => setError(errMsg(e, 'Could not load'))) }, [])
  useEffect(load, [load])
  const act = async (fn: () => Promise<unknown>) => { setError(null); try { await fn(); load() } catch (e) { setError(errMsg(e, 'Change failed')) } }
  return (
    <>
      <p className="tf-note">Choose the languages your team can use, and which one new users start with.</p>
      {error && <p className="tf-error">{error}</p>}
      <table className="tf-table">
        <thead><tr><th>Language</th><th>Status</th><th /></tr></thead>
        <tbody>
          {langs.map((l) => (
            <tr key={l.code}>
              <td>{l.label}</td>
              <td>{l.enabled ? (l.is_default ? 'Enabled · default' : 'Enabled') : 'Off'}</td>
              <td className="tf-row-actions">
                {!l.enabled && <button type="button" onClick={() => void act(() => api.post(`/languages/${l.code}/enable`))}>Enable</button>}
                {l.enabled && !l.is_default && <button type="button" onClick={() => void act(() => api.put(`/languages/${l.code}/default`))}>Make default</button>}
                {l.enabled && !l.is_default && <button type="button" onClick={() => void act(() => api.post(`/languages/${l.code}/disable`))}>Disable</button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  )
}

// --- Operational masters ----------------------------------------------------

const OP_TABS: Array<{ key: string; label: string; config?: MasterScreenConfig }> = [
  { key: 'vehicle-types', label: 'Vehicle Types', config: vehicleTypesConfig },
  { key: 'charge-types', label: 'Charge Types', config: chargeTypesConfig },
  { key: 'expense-heads', label: 'Expense Heads', config: expenseHeadsConfig },
  { key: 'accounting-heads', label: 'Accounting Heads', config: accountingHeadsConfig },
  { key: 'numbering', label: 'Numbering Sequences' },
]

function MastersTab() {
  const [active, setActive] = useState(OP_TABS[0]!.key)
  const t = OP_TABS.find((x) => x.key === active)!
  return (
    <>
      <div className="tf-subtabs">
        {OP_TABS.map((x) => (
          <button key={x.key} type="button" className={x.key === active ? 'tf-tab-active' : ''} onClick={() => setActive(x.key)}>{x.label}</button>
        ))}
      </div>
      {t.config ? <MasterScreen key={t.key} config={t.config} /> : <NumberingPanel />}
    </>
  )
}

interface Seq { id: string; branch_id: string | null; series_type: string; prefix: string | null; current_number: number; padding: number; reset_frequency: string }

function NumberingPanel() {
  const [rows, setRows] = useState<Seq[]>([])
  const [branches, setBranches] = useState<Record<string, string>>({})
  const [edit, setEdit] = useState<Record<string, { prefix: string; padding: string; reset_frequency: string; current_number: string }>>({})
  const [error, setError] = useState<string | null>(null)
  const load = useCallback(() => {
    api.get<Seq[]>('/numbering-sequences').then(setRows).catch((e) => setError(errMsg(e, 'Could not load')))
    api.get<{ rows: BranchRow[] }>('/branches?pageSize=100').then((b) => setBranches(Object.fromEntries(b.rows.map((x) => [x.id, x.name])))).catch(() => {})
  }, [])
  useEffect(load, [load])

  const save = async (s: Seq) => {
    const e = edit[s.id]
    if (!e) return
    setError(null)
    try {
      await api.put(`/numbering-sequences/${s.id}`, { prefix: e.prefix, padding: Number(e.padding), reset_frequency: e.reset_frequency, current_number: Number(e.current_number) })
      setEdit(({ [s.id]: _gone, ...rest }) => rest)
      load()
    } catch (err) {
      setError(errMsg(err, 'Save failed'))
    }
  }
  const startEdit = (s: Seq) => setEdit({ ...edit, [s.id]: { prefix: s.prefix ?? '', padding: String(s.padding), reset_frequency: s.reset_frequency, current_number: String(s.current_number) } })

  return (
    <>
      <p className="tf-note">Document numbers (LR, invoice, receipt…). The current number can be raised to line up with a paper series, but never lowered.</p>
      {error && <p className="tf-error">{error}</p>}
      <table className="tf-table">
        <thead><tr><th>Series</th><th>Branch</th><th>Prefix</th><th>Padding</th><th>Resets</th><th>Last used</th><th /></tr></thead>
        <tbody>
          {rows.map((s) => {
            const e = edit[s.id]
            const up = (k: keyof NonNullable<typeof e>, v: string) => setEdit({ ...edit, [s.id]: { ...e!, [k]: v } })
            return (
              <tr key={s.id}>
                <td>{s.series_type}</td>
                <td>{s.branch_id ? branches[s.branch_id] ?? '—' : 'All'}</td>
                <td>{e ? <input value={e.prefix} onChange={(ev) => up('prefix', ev.target.value)} size={8} /> : s.prefix}</td>
                <td>{e ? <input type="number" value={e.padding} onChange={(ev) => up('padding', ev.target.value)} style={{ width: '4rem' }} /> : s.padding}</td>
                <td>{e ? (
                  <select value={e.reset_frequency} onChange={(ev) => up('reset_frequency', ev.target.value)}>
                    {['Never', 'Yearly', 'Monthly'].map((x) => <option key={x}>{x}</option>)}
                  </select>) : s.reset_frequency}</td>
                <td>{e ? <input type="number" value={e.current_number} onChange={(ev) => up('current_number', ev.target.value)} style={{ width: '6rem' }} /> : s.current_number}</td>
                <td className="tf-row-actions">
                  {e ? (<><button type="button" onClick={() => void save(s)}>Save</button><button type="button" onClick={() => setEdit(({ [s.id]: _g, ...r }) => r)}>Cancel</button></>) : <button type="button" onClick={() => startEdit(s)}>Edit</button>}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </>
  )
}

// --- Integrations -----------------------------------------------------------

interface EwaySettings { generation_mode: string; road_distance_correction_factor: string | number; alert_hours_before_expiry: number[] }
interface Schedule { id: string; recipient_number: string; report_type: string; frequency: string; send_time: string; verified_at: string | null; is_active: boolean }
interface LogRow { id: string; sent_at: string; recipient: string | null; status: string; failure_reason: string | null; attempt: number; kind: string; report_type: string | null }

function IntegrationsTab() {
  return (
    <>
      <EwayCard />
      <WhatsAppCard />
    </>
  )
}

function EwayCard() {
  const [s, setS] = useState<EwaySettings | null>(null)
  const [factor, setFactor] = useState('')
  const [hours, setHours] = useState('')
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null)
  useEffect(() => {
    api.get<EwaySettings>('/settings/eway').then((r) => { setS(r); setFactor(String(r.road_distance_correction_factor)); setHours(r.alert_hours_before_expiry.join(', ')) }).catch((e) => setMsg({ ok: false, text: errMsg(e, 'Could not load') }))
  }, [])
  const save = async (e: FormEvent) => {
    e.preventDefault()
    setMsg(null)
    const list = hours.split(',').map((x) => Number(x.trim())).filter((n) => Number.isFinite(n) && n > 0)
    try {
      const r = await api.put<EwaySettings>('/settings/eway', { road_distance_correction_factor: Number(factor), alert_hours_before_expiry: list })
      setS(r); setHours(r.alert_hours_before_expiry.join(', '))
      setMsg({ ok: true, text: 'Saved.' })
    } catch (err) {
      setMsg({ ok: false, text: errMsg(err, 'Save failed') })
    }
  }
  return (
    <form className="tf-card tf-form" onSubmit={save}>
      <h2>E-Way Bill</h2>
      <p className="tf-note">Bills are entered manually. Automatic generation through a GSP is not switched on yet.</p>
      <div className="tf-grid-2">
        <label className="tf-field">Mode<input value={s?.generation_mode ?? ''} readOnly /></label>
        <label className="tf-field">Road distance factor<input value={factor} onChange={(e) => setFactor(e.target.value)} /></label>
        <label className="tf-field">Alert hours before expiry<input value={hours} onChange={(e) => setHours(e.target.value)} placeholder="24, 12, 6" />
          <span className="tf-note">The earliest value is when the “expiring” alert first appears.</span></label>
      </div>
      {msg && <p className={msg.ok ? 'tf-note' : 'tf-error'}>{msg.text}</p>}
      <div className="tf-form-actions"><button className="tf-button-primary" type="submit">Save</button></div>
    </form>
  )
}

function WhatsAppCard() {
  const [rows, setRows] = useState<Schedule[]>([])
  const [log, setLog] = useState<LogRow[]>([])
  const [form, setForm] = useState({ recipient_number: '', report_type: 'Both', frequency: 'Daily', send_time: '09:00' })
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  const load = useCallback(() => {
    api.get<Schedule[]>('/notification-settings').then(setRows).catch((e) => setMsg({ ok: false, text: errMsg(e, 'Could not load') }))
    api.get<{ rows: LogRow[] }>('/notification-log?pageSize=20').then((r) => setLog(r.rows)).catch(() => {})
  }, [])
  useEffect(load, [load])

  const run = async (id: string, fn: () => Promise<unknown>, ok: string) => {
    setBusy(id); setMsg(null)
    try { await fn(); setMsg({ ok: true, text: ok }) } catch (e) { setMsg({ ok: false, text: errMsg(e, 'Failed') }) }
    setBusy(null); load()
  }

  const add = async (e: FormEvent) => {
    e.preventDefault()
    setMsg(null)
    try {
      await api.post('/notification-settings', form)
      setForm({ ...form, recipient_number: '' })
      setMsg({ ok: true, text: 'Added. Send a test message to confirm the number, then activate it.' })
      load()
    } catch (err) {
      setMsg({ ok: false, text: errMsg(err, 'Could not add') })
    }
  }

  return (
    <div className="tf-card">
      <h2>WhatsApp scheduled reports</h2>
      <p className="tf-note">A short summary with a link back to the full report, sent to the number below. A number must receive a test message before the schedule can be switched on. A failed send is retried once after 15 minutes, then you are alerted in the bell.</p>

      <form className="tf-form" onSubmit={add}>
        <div className="tf-grid-2">
          <label className="tf-field">WhatsApp number<input required value={form.recipient_number} onChange={(e) => setForm({ ...form, recipient_number: e.target.value })} placeholder="98xxxxxxxx" /></label>
          <label className="tf-field">Report
            <select value={form.report_type} onChange={(e) => setForm({ ...form, report_type: e.target.value })}>
              <option value="Both">Receivables &amp; Payables</option><option value="Receivables">Receivables</option><option value="Payables">Payables</option>
            </select>
          </label>
          <label className="tf-field">How often
            <select value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })}>
              <option value="Daily">Every day</option><option value="Weekly">Every Monday</option><option value="Monthly">1st of each month</option>
            </select>
          </label>
          <label className="tf-field">Send at (IST)<input type="time" value={form.send_time} onChange={(e) => setForm({ ...form, send_time: e.target.value })} /></label>
        </div>
        <div className="tf-form-actions"><button className="tf-button-primary" type="submit">Add schedule</button></div>
      </form>

      {msg && <p className={msg.ok ? 'tf-note' : 'tf-error'}>{msg.text}</p>}

      <table className="tf-table" style={{ marginTop: '1rem' }}>
        <thead><tr><th>Number</th><th>Report</th><th>When</th><th>Number checked</th><th>Status</th><th /></tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={6}>No schedules yet.</td></tr>}
          {rows.map((r) => (
            <tr key={r.id}>
              <td>+{r.recipient_number}</td>
              <td>{r.report_type}</td>
              <td>{r.frequency} {r.send_time}</td>
              <td>{r.verified_at ? 'Yes' : 'No'}</td>
              <td><span className={`tf-pill ${r.is_active ? 'tf-pill-ok' : ''}`}>{r.is_active ? 'Active' : 'Off'}</span></td>
              <td className="tf-row-actions">
                <button type="button" disabled={busy === r.id} onClick={() => void run(r.id, () => api.post(`/notification-settings/${r.id}/test`), 'Test message sent — number confirmed.')}>Send test</button>
                {r.is_active
                  ? <button type="button" disabled={busy === r.id} onClick={() => void run(r.id, () => api.put(`/notification-settings/${r.id}`, { is_active: false }), 'Schedule switched off.')}>Turn off</button>
                  : <button type="button" disabled={busy === r.id || !r.verified_at} title={r.verified_at ? '' : 'Send a test message first'} onClick={() => void run(r.id, () => api.put(`/notification-settings/${r.id}`, { is_active: true }), 'Schedule is on.')}>Turn on</button>}
                <button type="button" disabled={busy === r.id} onClick={() => { if (window.confirm('Remove this schedule?')) void run(r.id, () => api.del(`/notification-settings/${r.id}`), 'Removed.') }}>Remove</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2 style={{ marginTop: '1.25rem' }}>Recent sends</h2>
      <table className="tf-table">
        <thead><tr><th>When</th><th>To</th><th>Kind</th><th>Attempt</th><th>Result</th></tr></thead>
        <tbody>
          {log.length === 0 && <tr><td colSpan={5}>Nothing sent yet.</td></tr>}
          {log.map((l) => (
            <tr key={l.id}>
              <td>{new Date(l.sent_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</td>
              <td>{l.recipient ? `+${l.recipient}` : ''}</td>
              <td>{l.kind === 'Test' ? 'Test message' : l.report_type ?? 'Report'}</td>
              <td>{l.attempt}</td>
              <td><span className={`tf-pill ${l.status === 'Sent' ? 'tf-pill-ok' : 'tf-pill-bad'}`}>{l.status}</span>{l.failure_reason ? ` ${l.failure_reason}` : ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// --- Audit link -------------------------------------------------------------

function AuditLink() {
  const { go } = useNav()
  const { can } = useAuth()
  return (
    <div className="tf-card">
      <h2>Audit Trail</h2>
      <p className="tf-note">See who created, changed or deactivated any record, with the before and after values.</p>
      {can('audit_log.read')
        ? <button className="tf-button-primary" type="button" onClick={() => go('audit')}>Open the Audit Trail</button>
        : <p className="tf-note">You do not have access to the Audit Trail.</p>}
    </div>
  )
}
